import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Facebook, Instagram } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SEO } from "@/components/SEO";
import { PageHero, SectionTitle } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["General", "Joining / Registration", "Coaching", "Sponsorship", "Media", "Other"];

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "General", message: "", consent: false });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the privacy consent.");
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", category: "General", message: "", consent: false });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const input = "w-full rounded-lg border border-slate-300 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:border-[#1E63D6] focus:outline-none focus:ring-2 focus:ring-[#1E63D6]/20";
  const tel = (p) => (p || "").replace(/\s/g, "");
  const mapQuery = encodeURIComponent(settings?.address || "Brackenfell, Cape Town, 7560");

  return (
    <>
      <SEO title="Contact | Brackenfell Junior Rugby Club" description="Get in touch with Brackenfell Junior Rugby Club. Phone, email, address and enquiry form." />
      <PageHero title="Contact Us" subtitle="We'd love to hear from you. Reach out with any questions." />
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-5 lg:px-8">
        <div className="space-y-4 lg:col-span-2">
          <SectionTitle eyebrow="Get in Touch" title="Club Contacts" />
          <div className="space-y-3">
            {[["Director", settings?.phone_director], ["Club Organiser", settings?.phone_organiser], ["Administration", settings?.phone_admin]].filter(([, p]) => p).map(([label, p]) => (
              <a key={label} href={`tel:${tel(p)}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-[#1E63D6]" data-testid={`contact-phone-${label.toLowerCase().replace(/\s/g,"-")}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#0B2A4A] text-[#E8A317]"><Phone className="h-5 w-5" /></div>
                <div><div className="text-xs font-semibold uppercase text-slate-400">{label}</div><div className="font-bold text-[#0B2A4A]">{p}</div></div>
              </a>
            ))}
            {[settings?.email_admin, settings?.email_organiser].filter(Boolean).map((em) => (
              <a key={em} href={`mailto:${em}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-[#1E63D6]" data-testid={`contact-email-${em}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#0B2A4A] text-[#E8A317]"><Mail className="h-5 w-5" /></div>
                <div className="font-bold text-[#0B2A4A]">{em}</div>
              </a>
            ))}
            {settings?.address && (
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#0B2A4A] text-[#E8A317]"><MapPin className="h-5 w-5" /></div>
                <div className="font-semibold text-slate-700">{settings.address}</div>
              </div>
            )}
            {settings?.whatsapp && (
              <a href={`https://wa.me/${tel(settings.whatsapp).replace(/^0/, "27")}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] p-4 font-bold text-white" data-testid="contact-whatsapp">
                <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
              </a>
            )}
            {(settings?.facebook || settings?.instagram) && (
              <div className="flex gap-3" data-testid="contact-social">
                {settings?.facebook && (
                  <a href={settings.facebook} target="_blank" rel="noreferrer noopener" aria-label="Facebook" data-testid="contact-facebook"
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#1877F2] p-4 font-bold text-white transition-all hover:opacity-90">
                    <Facebook className="h-5 w-5" /> Facebook
                  </a>
                )}
                {settings?.instagram && (
                  <a href={settings.instagram} target="_blank" rel="noreferrer noopener" aria-label="Instagram" data-testid="contact-instagram"
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#E4405F] p-4 font-bold text-white transition-all hover:opacity-90">
                    <Instagram className="h-5 w-5" /> Instagram
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <iframe title="BJRC location" width="100%" height="260" style={{ border: 0 }} loading="lazy" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} />
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm lg:col-span-3" data-testid="contact-form">
          <h3 className="font-heading text-2xl font-extrabold uppercase text-[#0B2A4A]">Send an Enquiry</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <input required placeholder="Your name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            <input placeholder="Phone number" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className={input} data-testid="contact-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <textarea required placeholder="Your message" rows={5} className={`${input} mt-4`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
            <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="contact-consent" />
            <span>I consent to BJRC storing and processing my details to respond to this enquiry, in line with POPIA. We will not share your information.</span>
          </label>
          <button disabled={sending} type="submit" data-testid="contact-submit-button" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E8A317] py-3.5 font-extrabold uppercase text-[#0A1626] hover:bg-[#CC8F12] disabled:opacity-60">
            <Send className="h-5 w-5" /> {sending ? "Sending..." : "Send Message"}
          </button>
        </form>
      </section>
    </>
  );
}
